/**
 * @namespace transform/string
 * @see module:@kids/lib/transform/string
 */

/**
 * @module @kids/lib/transform/string
 * @see transform/string
 * @desc Stellt alle puren Funktionen zur String-Umwandlung zur Verfügung.
 * @example
 * import {getSecondsFromTimeString} from '@kids/lib/transform/string';
 *
 * getSecondsFromTimeString('12:34'); // -> 754
 */

const {parseInt} = globalThis;

/** Wandelt das erste Zeichen zu einem Großbuchstaben um.
 * @alias transform/string.upperCaseFirst
 * @param {string} string
 * @return {string}
 */
export function upperCaseFirst(string)
{
    return string.charAt(0).toUpperCase() + string.slice(1);
}

/** Wandelt das erste Zeichen zu einem Kleinbuchstaben um.
 * @alias transform/string.lowerCaseFirst
 * @param {string} string
 * @return {string}
 */
export function lowerCaseFirst(string)
{
    return string.charAt(0).toLowerCase() + string.slice(1);
}

/** Findet das erste Vorkommen einer ganzen Zahl in einem String und liefert diese zurück.
 * @alias transform/string.getInteger
 * @param {string} string
 * @return {number|NaN}
 */
export function getInteger(string)
{
    const result = string.match(/[0-9]+/);
    if (result !== null)
    {
        return parseInt(result[0], 10);
    }
    return NaN;
}

/** Wandelt einen Zeit-String der Form ``02:10`` oder ``02:10:00`` in Sekunden um.
 * @alias transform/string.getSecondsFromTimeString
 * @param {string} string
 * @param {string} [format="mm:ss"] Ein Format, welches die Platzhalter H, HH, m, mm, s und ss nutzen kann.
 * @return {number|NaN}
 */
export function getSecondsFromTimeString(string, format = 'mm:ss')
{
    const units = {
        hours: 0,
        minutes: 0,
        seconds: 0
    };
    const positions = [];
    const regExp = escapeRegExp(format).replace(/H{1,2}|m{1,2}|s{1,2}/g, (match) =>
    {
        switch (match)
        {
            case 'mm':
            case 'm':
                positions.push('minutes'); break;
            case 'ss':
            case 's':
                positions.push('seconds'); break;
            case 'HH':
            case 'H':
                positions.push('hours'); break;
            default:
        }
        return `(\\d{${match.length === 1 ? '1,2' : '2'}})`;
    });
    if (!positions.length) { return NaN; }

    const matches = string.match(new RegExp(regExp));
    if (matches === null) { return NaN; }

    positions.forEach((unit, index) =>
    {
        units[unit] = parseInt(matches[index + 1], 10);
    });

    return units.seconds + (units.minutes * 60) + (units.hours * 3600);
}

/** Escapt alle Funktionszeichen von regulären Ausdrücken, damit sie in einer ``RegExp`` suchbar werden.
 * @alias transform/string.escapeRegExp
 * @param {string} string
 * @return {string}
 */
export function escapeRegExp(string)
{
    return string.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
}

const HTML_ESCAPE_MAP = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
};

/** Escapt &, ", ', <, > zu HTML Entities.
 * @alias transform/string.escapeHtml
 * @param {string} string
 * @param {boolean} [quotes=true]
 * @return {string}
 */
export function escapeHtml(string, quotes = true)
{
    return string.replace(quotes ? /[&<>'"]/g : /[&<>]/g, match => HTML_ESCAPE_MAP[match]);
}

/** Macht einen Camel- zu eine Kebab-Case-String.
 * @alias transform/string.camelToKebabCase
 * @param {string} string
 * @return {string}
 */
export function camelToKebabCase(string)
{
    return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}
